import { Injectable, SecurityContext } from '@angular/core';
import { getFormattedAddress, getFormattedAddresses } from 'common/emails/email-address-helper';
import moment from 'moment';
import { EmailMessage } from 'common/store/genesys-cloud/conversations/emails/models/email-message.model';
import { DomSanitizer } from '@angular/platform-browser';
import { LinkTargetBlankFilter } from 'common/html-helper/link-target-blank';
import { Attachment } from 'common/store/genesys-cloud/conversations/emails/models/attachment.model';
import { EmailAddress } from 'common/store/genesys-cloud/conversations/emails/models/email-address.model';

@Injectable()
export class GenesysEmailComposeService {
  private readonly _linkTargetBlankFilter = LinkTargetBlankFilter();
  constructor(private readonly _sanitizer: DomSanitizer) {}

  buildSubjectForSend(subject: string = '', prefix: string): string {
    const existingPrefix = subject.substring(0, 4).toUpperCase();
    const remainingSubject = subject.substring(4);
    switch (existingPrefix) {
      case 'RE: ':
        return prefix === 'FW' ? `FW: ${remainingSubject}` : subject;
      case 'FW: ':
        return prefix === 'RE' ? `RE: ${remainingSubject}` : subject;
      default:
        return `${prefix}: ${subject}`;
    }
  }

  getAttachmentMatches(lastEmailAttachments: Attachment[], draftAttachments: Attachment[]): Map<string, string> {
    let matches = new Map<string, string>();
    if (lastEmailAttachments?.length && draftAttachments?.length) {
      lastEmailAttachments
        .filter((lastEmailAttachment) => lastEmailAttachment.inlineImage)
        .forEach((value) =>
          draftAttachments
            .filter((draftValue) => value.name === draftValue.name && value.contentLength === draftValue.contentLength)
            .forEach((draftValue) => matches.set(value.contentUri, draftValue.attachmentId))
        );
    }
    return matches;
  }

  addOriginalEmailMessageForMessage(htmlBody: string, lastEmail: EmailMessage) {
    const emailInfo = this._getEmailHeadersInSafeFormat(lastEmail);
    const originalEmailMessage = this._getEmailBody(lastEmail);
    const composedMessage = this._sanitizer.sanitize(SecurityContext.HTML, htmlBody);

    return `${composedMessage}<br />
            <br />
            ----- Original Message -----<br />
            From: ${emailInfo.senderInfo}<br />
            Date: ${emailInfo.sentDate}<br />
            To: ${emailInfo.receiverInfo}<br />
            Subject: ${emailInfo.subject}<br />
            <br />
            ${originalEmailMessage}`;
  }

  replaceContentUri(body: string, matches: Map<string, string>): string {
    matches.forEach(function (k, v) {
      body = body.replace(new RegExp(v, 'g'), 'cid:' + k);
    });

    return body;
  }

  removeAgentEmailFromRecipients(addresses: EmailAddress[], agentEmail: string): EmailAddress[] {
    return addresses.filter((email) => email.email !== agentEmail);
  }

  getReplyDefaultComposedEmail(
    lastEmail: EmailMessage,
    agentEmailAddress: EmailAddress,
    draftEmail: EmailMessage,
    isLastEmailFromCustomer: boolean
  ): Partial<EmailMessage> {
    return {
      from: agentEmailAddress || null,
      textBody: draftEmail?.textBody || '',
      htmlBody: draftEmail?.htmlBody || '',
      to: isLastEmailFromCustomer ? [lastEmail.replyTo || lastEmail.from] : lastEmail.to,
      cc: [],
      bcc: [],
      subject: this.buildSubjectForSend(lastEmail.subject, 'RE')
    };
  }

  getReplyAllDefaultComposedEmail(
    lastEmail: EmailMessage,
    agentEmailAddress: EmailAddress,
    draftEmail: EmailMessage,
    isLastEmailFromCustomer: boolean
  ): Partial<EmailMessage> {
    return {
      from: agentEmailAddress || null,
      to: isLastEmailFromCustomer
        ? [
            lastEmail.replyTo || lastEmail.from,
            ...this.removeAgentEmailFromRecipients(lastEmail.to, agentEmailAddress.email)
          ]
        : this.removeAgentEmailFromRecipients(lastEmail.to, agentEmailAddress.email),
      cc: this.removeAgentEmailFromRecipients(lastEmail.cc, agentEmailAddress.email),
      bcc: [],
      subject: this.buildSubjectForSend(lastEmail.subject, 'RE'),
      textBody: draftEmail?.textBody || '',
      htmlBody: draftEmail?.htmlBody || ''
    };
  }

  getForwardDefaultComposedEmail(
    subject: string,
    agentEmailAddress: EmailAddress,
    draftEmail: EmailMessage
  ): Partial<EmailMessage> {
    return {
      from: agentEmailAddress || null,
      to: [],
      cc: [],
      bcc: [],
      subject: this.buildSubjectForSend(subject, 'FW'),
      textBody: draftEmail?.textBody || '',
      htmlBody: draftEmail?.htmlBody || ''
    };
  }

  getDefaultComposedEmail(draftEmail: EmailMessage, agentEmailAddress: EmailAddress): Partial<EmailMessage> {
    return {
      from: agentEmailAddress || null,
      to: [],
      cc: [],
      bcc: [],
      subject: '',
      textBody: draftEmail?.textBody || '',
      htmlBody: draftEmail?.htmlBody || ''
    };
  }

  getEmailValidatorErrorMessage(emailMessage: Partial<EmailMessage>): string {
    if (!emailMessage.textBody && !emailMessage.htmlBody) {
      return 'genesysEmailFooter.noHtmlBodyOrTextBodyError';
    }
    return '';
  }

  private _getEmailHeadersInSafeFormat(email: EmailMessage) {
    return {
      senderInfo: this._buildTextWithCharacterEntities(getFormattedAddress(email.from)),
      receiverInfo: this._buildTextWithCharacterEntities(getFormattedAddresses(email.to)),
      subject: this._buildTextWithCharacterEntities(email.subject),
      sentDate: moment(email.time).format('LLL')
    };
  }

  private _getEmailBody(email: EmailMessage): string {
    if (email.altHtmlBody) {
      return this._sanitizer.sanitize(SecurityContext.HTML, this._linkTargetBlankFilter(email.altHtmlBody));
    }

    return email.textBody;
  }

  private _buildTextWithCharacterEntities(address: string): string {
    return address.replace(/</g, '&lt;').replace(/>/g, '&gt;');
  }
}
